import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import Utils from '../utils/Utils';

// Колко отваряния на менюто се пропускат, след като клиентът затвори банера.
// След тях банерът се показва отново (и пак, ако бъде затворен).
const SKIP_OPENINGS_AFTER_CLOSE = 3;

// Изчакване преди показване, за да не се бие с началния банер / зареждането.
const SHOW_DELAY_MS = 2500;

// Резервни текстове, ако преводите още не са заредени или езикът няма ключа.
const FALLBACK = {
    title: 'Добави менюто на началния екран',
    body: 'Отваряй го като приложение — на цял екран, бързо и без търсене.',
    ios_title: 'Добави менюто на началния екран',
    ios_body: 'Натисни „Сподели“ долу и избери „Към началния екран“.',
    ios_chrome_body: 'Натисни иконата „Сподели“ в адресната лента и избери „Добави към начален екран“.',
    ios_other_body: 'Отвори менюто на браузъра, натисни „Сподели“ и избери „Към началния екран“.',
    install: 'Добави',
    close: 'Затвори'
};

function getSlug() {
    let seg = (window.location.pathname.split('/')[1] || '').trim();
    return seg || 'default';
}

function isStandalone() {
    try {
        return (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
            window.navigator.standalone === true;
    } catch (e) {
        return false;
    }
}

// Кой браузър на iOS: 'safari' | 'chrome' | 'other' | null (не е iOS или не може).
// От iOS 16.4 и Chrome/Edge/Firefox могат "Към началния екран" през Сподели;
// вградените браузъри (Instagram, Facebook, Viber...) не могат.
function iosBrowser() {
    let ua = window.navigator.userAgent || '';
    if (!/iphone|ipad|ipod/i.test(ua)) return null;
    if (/FBAN|FBAV|FB_IAB|Instagram|Messenger|Viber|Line\/|MicroMessenger|Snapchat|TikTok|musical_ly|GSA\//i.test(ua)) return null;

    let isChrome = /crios/i.test(ua);
    let isOther = /fxios|edgios|opios|opt\//i.test(ua);
    if (isChrome || isOther) {
        let v = /OS (\d+)_(\d+)/i.exec(ua);
        let major = v ? parseInt(v[1], 10) : 0;
        let minor = v ? parseInt(v[2], 10) : 0;
        if (major < 16 || (major === 16 && minor < 4)) return null;
        return isChrome ? 'chrome' : 'other';
    }

    return /safari/i.test(ua) ? 'safari' : null;
}

// Бутонът "Добави" ползва темата на обекта (pwa_theme_color). Ако цветът е светъл,
// текстът върху него става тъмен, за да не изчезне (старият проблем с белия фон).
function accentColors(hex) {
    let m = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.exec(hex || '');
    if (!m) return { bg: '#111111', fg: '#ffffff' };

    let h = m[1];
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');

    let r = parseInt(h.substr(0, 2), 16);
    let g = parseInt(h.substr(2, 2), 16);
    let b = parseInt(h.substr(4, 2), 16);
    let luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    return { bg: '#' + h, fg: luminance > 0.62 ? '#1c1c1e' : '#ffffff' };
}

// Тестов режим: ?a2hs=1 показва банера принудително (пренебрегва настройката,
// брояча за пропуснати отваряния и липсата на нативен install prompt).
// ?a2hs=reset само нулира брояча. Логовете са с префикс [A2HS].
function getForceMode() {
    try {
        let v = new URLSearchParams(window.location.search).get('a2hs');
        if (v === '1' || v === 'test') return 'force';
        if (v === 'reset') return 'reset';
    } catch (e) { /* ignore */ }
    return null;
}

function log(reason) {
    try { console.log('[A2HS] ' + reason); } catch (e) { /* ignore */ }
}

function lsGet(key) {
    try { return localStorage.getItem(key); } catch (e) { return null; }
}
function lsSet(key, value) {
    try { localStorage.setItem(key, value); } catch (e) { /* ignore */ }
}
function ssGet(key) {
    try { return sessionStorage.getItem(key); } catch (e) { return null; }
}
function ssSet(key, value) {
    try { sessionStorage.setItem(key, value); } catch (e) { /* ignore */ }
}

function AddToHomeScreen({ enabled, t, themeColor }) {
    const [visible, setVisible] = useState(false);
    const [prompt, setPrompt] = useState(window.__a2hsPrompt || null);
    const [iconOk, setIconOk] = useState(true);
    const decidedRef = useRef(false);
    const timerRef = useRef(null);

    const slug = getSlug();
    const skipKey = 'a2hs_skip_' + slug;          // оставащи пропуснати отваряния
    const sessionKey = 'a2hs_session_' + slug;    // решението за текущата сесия
    const force = getForceMode() === 'force';

    // Хвани "beforeinstallprompt" (Android/Chrome). Събитието може да е дошло още
    // преди React да се е монтирал — тогава index.js го е запазил в window.__a2hsPrompt.
    useEffect(() => {
        const onBeforeInstall = (e) => {
            e.preventDefault();
            window.__a2hsPrompt = e;
            setPrompt(e);
        };
        const onCaptured = () => setPrompt(window.__a2hsPrompt || null);
        const onInstalled = () => {
            window.__a2hsPrompt = null;
            setPrompt(null);
            setVisible(false);
        };

        window.addEventListener('beforeinstallprompt', onBeforeInstall);
        window.addEventListener('a2hs-prompt-captured', onCaptured);
        window.addEventListener('appinstalled', onInstalled);

        return () => {
            window.removeEventListener('beforeinstallprompt', onBeforeInstall);
            window.removeEventListener('a2hs-prompt-captured', onCaptured);
            window.removeEventListener('appinstalled', onInstalled);
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    // Диагностика: ако след зареждането на настройките банерът е изключен, кажи го
    // в конзолата — иначе "не работи" изглежда еднакво с "не е пуснато".
    useEffect(() => {
        const timer = setTimeout(() => {
            if (!enabled && !force) {
                log('изключен: настройката show_add_to_home_screen_banner за този обект е 0 ' +
                    '(включи я от админа → Настройки).');
            }
        }, 6000);
        return () => clearTimeout(timer);
    }, [enabled, force]);

    // Реши веднъж на сесия (едно отваряне на менюто = една сесия на таба) дали
    // банерът да се покаже. Настройките идват асинхронно, затова следим `enabled`.
    useEffect(() => {
        if (decidedRef.current) return;

        if (getForceMode() === 'reset') {
            lsSet(skipKey, '0');
            ssSet(sessionKey, '');
            log('броячът е нулиран (?a2hs=reset)');
        }

        if (!enabled && !force) return; // настройките още не са дошли или е изключено

        decidedRef.current = true;

        if (isStandalone() && !force) {
            log('скрит: менюто вече е инсталирано (standalone режим)');
            return;
        }

        let decision = force ? 'show' : ssGet(sessionKey);
        if (!decision) {
            let skip = parseInt(lsGet(skipKey), 10) || 0;
            if (skip > 0) {
                lsSet(skipKey, String(skip - 1));
                decision = 'hide';
                log('скрит: остават още ' + (skip - 1) + ' отваряния след затварянето');
            } else {
                decision = 'show';
            }
            ssSet(sessionKey, decision);
        }

        if (decision === 'show') {
            timerRef.current = setTimeout(() => setVisible(true), force ? 0 : SHOW_DELAY_MS);
        } else {
            log('скрит за тази сесия (вече е затворен или пропуснат)');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, force]);

    // Затваряне => пропусни следващите N отваряния.
    const dismiss = useCallback(() => {
        setVisible(false);
        lsSet(skipKey, String(SKIP_OPENINGS_AFTER_CLOSE));
        ssSet(sessionKey, 'hide');
    }, [skipKey, sessionKey]);

    const handleInstall = () => {
        setVisible(false);
        ssSet(sessionKey, 'hide');
        if (!prompt) {
            log('няма нативен install prompt — бутонът е само визуален (тестов режим)');
            return;
        }

        prompt.prompt();
        if (prompt.userChoice) {
            prompt.userChoice
                .then((choice) => {
                    // Отказал е нативния диалог => третираме го като затваряне.
                    if (!choice || choice.outcome !== 'accepted') {
                        lsSet(skipKey, String(SKIP_OPENINGS_AFTER_CLOSE));
                    }
                })
                .catch(() => { });
        }
        window.__a2hsPrompt = null;
        setPrompt(null);
    };

    if (!visible) return null;
    if (!enabled && !force) return null;

    // Android/Chrome => нативен install бутон. iOS => текстова инструкция според браузъра.
    const iosKind = !prompt ? iosBrowser() : null;
    const isIos = !!iosKind;
    const iosBody = iosKind === 'chrome' ? t.ios_chrome_body
        : iosKind === 'other' ? t.ios_other_body
            : t.ios_body;
    if (!prompt && !isIos && !force) {
        // Няма нативен prompt (браузърът не поддържа инсталиране, инкогнито режим,
        // липсва service worker или приложението вече е инсталирано).
        log('скрит: браузърът не предлага инсталиране (няма beforeinstallprompt). ' +
            'В инкогнито Chrome НИКОГА не го изстрелва. Тествай с ?a2hs=1 за визуална проверка.');
        return null;
    }

    const accent = accentColors(themeColor);
    const iconSrc = '/pwa-icon/192?place=' + encodeURIComponent(slug);

    return (
        <div style={styles.wrapper} className="a2hs-banner">
            <div style={styles.card}>
                {iconOk ? (
                    <img
                        src={iconSrc}
                        alt=""
                        style={styles.icon}
                        onError={() => setIconOk(false)}
                    />
                ) : null}

                <div style={styles.texts}>
                    <div style={styles.title}>{isIos ? t.ios_title : t.title}</div>
                    <div style={styles.body}>{isIos ? iosBody : t.body}</div>

                    {!isIos && (
                        <div style={styles.actions}>
                            <button
                                type="button"
                                onClick={handleInstall}
                                style={{ ...styles.installBtn, backgroundColor: accent.bg, color: accent.fg }}
                            >
                                {t.install}
                            </button>
                        </div>
                    )}
                </div>

                <button
                    type="button"
                    onClick={dismiss}
                    aria-label={t.close}
                    title={t.close}
                    style={styles.closeBtn}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                        <path
                            fill="currentColor"
                            d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                        />
                    </svg>
                </button>
            </div>
        </div>
    );
}

// Инлайн стилове нарочно — банерът не бива да зависи от темата на обекта или от
// глобалния CSS (старата версия излизаше бял текст на бял фон).
const styles = {
    wrapper: {
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 'calc(12px + env(safe-area-inset-bottom))',
        display: 'flex',
        justifyContent: 'center',
        padding: '0 12px',
        zIndex: 2147483000,
        pointerEvents: 'none'
    },
    card: {
        position: 'relative',
        pointerEvents: 'auto',
        boxSizing: 'border-box',
        width: '100%',
        maxWidth: 420,
        display: 'flex',
        alignItems: 'flex-start',
        padding: '14px 40px 14px 14px',
        borderRadius: 16,
        background: '#ffffff',
        color: '#1c1c1e',
        boxShadow: '0 10px 32px rgba(0,0,0,0.28), 0 1px 3px rgba(0,0,0,0.12)',
        border: '1px solid rgba(0,0,0,0.06)',
        fontFamily: 'inherit'
    },
    icon: {
        width: 44,
        height: 44,
        minWidth: 44,
        borderRadius: 10,
        objectFit: 'cover',
        background: '#f2f2f2',
        marginTop: 2,
        marginRight: 12 // вместо flex `gap` — за по-стари iOS Safari
    },
    texts: {
        flex: 1,
        minWidth: 0
    },
    title: {
        fontSize: 15,
        fontWeight: 600,
        lineHeight: 1.25,
        color: '#1c1c1e'
    },
    body: {
        fontSize: 13,
        lineHeight: 1.35,
        color: '#5f6368',
        marginTop: 3
    },
    actions: {
        display: 'flex',
        alignItems: 'center',
        marginTop: 10
    },
    installBtn: {
        border: 'none',
        borderRadius: 999,
        color: '#ffffff',
        fontSize: 14,
        fontWeight: 600,
        padding: '8px 18px',
        cursor: 'pointer',
        fontFamily: 'inherit',
        lineHeight: 1
    },
    closeBtn: {
        position: 'absolute',
        top: 6,
        right: 6,
        width: 28,
        height: 28,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: 'none',
        borderRadius: '50%',
        background: 'transparent',
        color: '#8a8a8e',
        cursor: 'pointer',
        padding: 0
    }
};

const mapStateToProps = (state) => {
    const settings = (state.settings && state.settings.settings) || {};
    const translations = (state.lang && state.lang.translations) || {};
    const fromLang = (translations.modals && translations.modals.add_to_home_screen) || {};

    return {
        enabled: Utils.getSettingsValue(settings, 'show_add_to_home_screen_banner') == 1,
        themeColor: Utils.getSettingsValue(settings, 'pwa_theme_color') || '',
        t: { ...FALLBACK, ...fromLang }
    };
};

export default connect(mapStateToProps)(AddToHomeScreen);
